import React from 'react';
import { useLocalizedText } from '../localizedText';
import { useLocale } from '../locale';
import { useTimezone } from '../timezone';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import { GetParticipationsByEventShiftCommandOutput, Shift } from '@amzn/red-velvet-api';
import { useParticipantsFilterProperties } from '../../components/attendance/wizard/tablePropertyFilter';
import { useAllPagesRedVelvetQuery } from '../allPagesRedVelvetQuery';
import { useParticipantSessionStorage } from './participantSessionStorage';
import { useBulkParticipantSessionStorage } from './bulkParticipantSessionStorage';
import { Amazonian, getAmazoniansFromParticipants } from '../../utils/attendance/peopleSearch';
import { formatShiftDate, formatShiftTime } from '../../utils/dateFormatting';

export function useParticipants(
  namespaces: string[],
  tableName: string,
  eventId: string,
  shiftId: string,
  pageSize: number,
  shifts?: Shift[]
) {
  const { t } = useLocalizedText(namespaces);
  const locale = useLocale();
  const timezone = useTimezone();

  const isEventLevelTracking = eventId && !shiftId;

  // Create a map of shiftId -> Shift for quick lookup (used for event-level tracking)
  const shiftMap = React.useMemo(() => {
    const map = new Map<string, Shift>();
    (shifts || []).forEach((shift) => {
      if (shift.shiftId) {
        map.set(shift.shiftId, shift);
      }
    });
    return map;
  }, [shifts]);

  // Get all pages so we can de-deupe with attendees
  // Do NOT query this if there is no shiftId or eventId, because that indicates
  // we are in the process of creating the shift, and possibly the event as well
  // For event-level tracking (multi-shift), we query with eventId only.
  const participantResult = useAllPagesRedVelvetQuery({
    enabled: eventId !== '' && (shiftId !== '' || Boolean(isEventLevelTracking)),
    redVelvetQueryKey: ['getParticipationsByEventShift', { eventId, shiftId }],
    converter: (output: GetParticipationsByEventShiftCommandOutput) => {
      const items = (output.participations || []).map((p) => ({
        ...p,
      }));
      return { items, next: output.page };
    },
  });

  const rawParticipants = participantResult.data || [];

  const { participants: participantsUrlData } = useParticipantSessionStorage();
  const { bulkParticipants } = useBulkParticipantSessionStorage();

  /*
    Get event's participation data from getParticipationsByEventShift,
    merge with Attendees modal's participation data,
    and apply user manual changes from participantsUrlData
  */
  let bulkParticipantsAndShiftParticipants: Amazonian[] = [];

  // Get initial event's participants data from getParticipationsByEventShift
  bulkParticipantsAndShiftParticipants = getAmazoniansFromParticipants(rawParticipants, {});

  // For event-level tracking, map each participant's shiftId to its data
  if (isEventLevelTracking) {
    bulkParticipantsAndShiftParticipants = bulkParticipantsAndShiftParticipants.reduce<Amazonian[]>(
      (filteredParticipants, participant) => {
        const shift = participant.shiftId ? shiftMap.get(participant.shiftId) : undefined;
        if (shift?.name) {
          filteredParticipants.push({
            ...participant,
            shiftName: shift.name,
            shiftDate: formatShiftDate(shift.startDateTime, timezone, locale),
            shiftTime: formatShiftTime(shift.startDateTime, timezone, locale),
          });
        }
        return filteredParticipants;
      },
      []
    );
  }

  // Merge with bulkParticipants from Add attendees modal
  Object.entries(bulkParticipants).forEach(([key, data]) => {
    // For event-level tracking, match using alias and originalShiftId (to find the original row)
    const findIndex = isEventLevelTracking
      ? bulkParticipantsAndShiftParticipants.findIndex(
          (p) => p.alias === data.alias && p.shiftId === data.originalShiftId
        )
      : bulkParticipantsAndShiftParticipants.findIndex((p) => p.alias === key);

    if (findIndex >= 0) {
      bulkParticipantsAndShiftParticipants[findIndex] = {
        ...bulkParticipantsAndShiftParticipants[findIndex],
        ...data,
      };
    } else {
      // Add new participant, enriching with shift data for event-level tracking
      if (isEventLevelTracking && data.shiftId) {
        const shift = shiftMap.get(data.shiftId);
        // Only add if shift has a name
        if (shift?.name) {
          bulkParticipantsAndShiftParticipants.push({
            ...data,
            shiftName: shift.name,
            shiftDate: formatShiftDate(shift?.startDateTime, timezone, locale),
            shiftTime: formatShiftTime(shift?.startDateTime, timezone, locale),
          });
        }
      } else {
        bulkParticipantsAndShiftParticipants.push(data);
      }
    }
  });

  // Apply participantsUrlData from user manual changes with highest priority
  bulkParticipantsAndShiftParticipants.forEach((participant, index) => {
    if (participant.alias && participantsUrlData[participant.alias]) {
      bulkParticipantsAndShiftParticipants[index] = {
        ...participant,
        ...participantsUrlData[participant.alias],
      };
    }
  });

  const filteringProperties = useParticipantsFilterProperties();
  const collectionData = useCollection<Amazonian>(bulkParticipantsAndShiftParticipants, {
    filtering: {
      empty: (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>
            {participantResult.isError
              ? t(`${tableName}.errorOccurred`)
              : t(`${tableName}.noneFound`)}
          </b>
        </Box>
      ),
    },
    propertyFiltering: {
      filteringProperties,
    },
    pagination: { pageSize: pageSize },
    sorting: {},
    selection: {
      // We should use alias (not name) as the unique identifier for selection tracking.
      // Aliases are -almost- guaranteed unique, but names can certainly have duplicates.
      // Without trackBy, useCollection uses object reference equality which breaks when new objects
      // are created on each render, causing selection state issues and items duplicating across pages.
      // For event-level tracking, use composite key (alias + shiftId) since same person can appear multiple times.
      trackBy: isEventLevelTracking
        ? (item) => `${item.alias}-${item.shiftId}`
        : (item) => item.alias || '',
      keepSelection: true,
    },
  });

  return {
    isError: participantResult.isError,
    isLoading: participantResult.isLoading,
    participants: bulkParticipantsAndShiftParticipants,
    rawParticipants,
    ...collectionData,
  };
}
